import { useCallback, useEffect, useRef, useState } from "react";
import { isClientError } from "../../context/classMemberLoginErrors";
import { rememberCodeReminder } from "../../context/classMemberStorage";
import { registerClassMember, type ClassMember } from "../../networking/schoolClasses";

export enum ClassMemberJoinSteps {
    ENTRY = "entry",
    JOIN = "join",
    WELCOME = "welcome",
}

export enum JoinErrorVariants {
    NONE = "none",
    REJECTED = "rejected",
    TRANSIENT = "transient",
}

interface UseClassMemberJoinParams {
    schoolClassId: string;
    login: (classCode: string, personalCode: string) => Promise<ClassMember>;
}

export interface ClassMemberJoin {
    step: ClassMemberJoinSteps;
    /** Uppercased for display. */
    loginCode: string;
    /** Name of the already created member; a retry may only repeat the login, not rename. */
    registeredName: string;
    isSubmitting: boolean;
    error: JoinErrorVariants;
    startJoin: () => void;
    cancelJoin: () => void;
    register: (name: string) => Promise<void>;
    finish: () => void;
    /** Like `finish`, but the code is shown again after the pupil's next submitted test. */
    finishAndRemindLater: () => void;
}

const joinErrorVariantFromError = (e: unknown): JoinErrorVariants =>
    isClientError(e) ? JoinErrorVariants.REJECTED : JoinErrorVariants.TRANSIENT;

/**
 * `register` logs the pupil in before showing WELCOME, so leaving the welcome screen early loses
 * nothing: the code shown there is for the next device.
 */
export const useClassMemberJoin = ({ schoolClassId, login }: UseClassMemberJoinParams): ClassMemberJoin => {
    const [step, setStep] = useState(ClassMemberJoinSteps.ENTRY);
    const [loginCode, setLoginCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(JoinErrorVariants.NONE);
    // Survives a failed login so that a retry repeats only the login instead of creating a second member.
    const registeredMember = useRef<ClassMember | null>(null);
    // From the login response: the registration response carries no member id.
    const loggedInMemberId = useRef<string | null>(null);
    const [registeredName, setRegisteredName] = useState("");

    const previousSchoolClassId = useRef(schoolClassId);
    useEffect(() => {
        if (previousSchoolClassId.current === schoolClassId) return;
        previousSchoolClassId.current = schoolClassId;
        // A successful login clears the revalidated class id, so the welcome screen must survive the change.
        if (step === ClassMemberJoinSteps.WELCOME) return;
        registeredMember.current = null;
        setRegisteredName("");
        setError(JoinErrorVariants.NONE);
        setStep(ClassMemberJoinSteps.ENTRY);
    }, [schoolClassId, step]);

    const startJoin = useCallback(() => {
        setError(JoinErrorVariants.NONE);
        setStep(ClassMemberJoinSteps.JOIN);
    }, []);

    const cancelJoin = useCallback(() => setStep(ClassMemberJoinSteps.ENTRY), []);

    const finish = useCallback(() => {
        registeredMember.current = null;
        loggedInMemberId.current = null;
        setRegisteredName("");
        setStep(ClassMemberJoinSteps.ENTRY);
    }, []);

    const finishAndRemindLater = useCallback(() => {
        if (loggedInMemberId.current) rememberCodeReminder(loggedInMemberId.current);
        finish();
    }, [finish]);

    const register = useCallback(
        async (name: string) => {
            setIsSubmitting(true);
            setError(JoinErrorVariants.NONE);
            try {
                registeredMember.current ??= await registerClassMember(schoolClassId, name);
                setRegisteredName(registeredMember.current.name);
            } catch (e) {
                setError(joinErrorVariantFromError(e));
                setIsSubmitting(false);
                return;
            }
            const { loginCode } = registeredMember.current;
            try {
                const loggedInMember = await login(schoolClassId, loginCode);
                loggedInMemberId.current = loggedInMember.id;
                setLoginCode(loginCode.toUpperCase());
                setStep(ClassMemberJoinSteps.WELCOME);
            } catch {
                setError(JoinErrorVariants.TRANSIENT);
            } finally {
                setIsSubmitting(false);
            }
        },
        [schoolClassId, login]
    );

    return {
        step,
        loginCode,
        registeredName,
        isSubmitting,
        error,
        startJoin,
        cancelJoin,
        register,
        finish,
        finishAndRemindLater,
    };
};
